import { useForm, UseFormReturn } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { AdmAuth } from "@/constants/code";


import type { EditFormProps } from ".";
import { AdmPwdUpdateRequestDto, type AdmUpdateRequestDto } from "@/dto/AdmDto";
import { Yn } from "@/constants/code";

export interface useEditFormProps {
  /**
   * 관리자 정보 수정 Form Hook
   * @type {UseFormReturn<AdmUpdateRequestDto>}
   */
  updateMethods: UseFormReturn<AdmUpdateRequestDto>;
  /**
   * 관리자 비밀번호 업데이트 Form Hook
   * @type {UseFormReturn<AdmPwdUpdateRequestDto>}
   */
  pwdUpdateMethods: UseFormReturn<AdmPwdUpdateRequestDto>;
  /**
   * 관리자 정보 업데이트 이벤트 핸들러
   * @returns {Promise<void>}
   */
  handleAdmUpdate: () => Promise<void>;
  /**
   * 관리자 비밀번호 업데이트 이벤트 핸들러
   * @returns {Promise<void>}
   */
  handleAdmPwdUpdate: () => Promise<void>;
}

/**
 * 관리자 수정 폼 Hook
 *
 * @param {EditFormProps} props
 * @returns
 * @author bart <bart@ymjlab.com>
 * @version 1.0
 * @since 2024-12-24
 */
export default function useEditForm({
  admInfo,
  onAdmUpdate,
  onAdmPwdUpdate,

}: EditFormProps): useEditFormProps {
  /**
   * 관리자 정보 수정에 대한 Validation 체크 Hook
   * @type {UseFormMethods<AdmUpdateRequestDto>}
   */
  const updateMethods = useForm<AdmUpdateRequestDto>({
    resolver: yupResolver<AdmUpdateRequestDto>(
      yup.object().shape({
        admNm: yup.string().min(2).required(),
        email: yup.string().email().required(),
        admAuth: yup.mixed<AdmAuth>().oneOf(Object.values(AdmAuth)).required(),
        useYn: yup.mixed<Yn>().oneOf([Yn.Y, Yn.N]).required(),
      }) as yup.ObjectSchema<AdmUpdateRequestDto>
    ),
    defaultValues: {
      admNm: admInfo?.admNm || "",
      email: admInfo?.email || "",
      admAuth: admInfo?.admAuth || AdmAuth.Admin,
      useYn: admInfo?.useYn || Yn.Y,
    },
  });

  /**
   * 관리자 비밀번호 업데이트에 대한 Validation 체크 Hook
   * @type {UseFormMethods<AdmPwdUpdateRequestDto>}
   */
  const pwdUpdateMethods = useForm({
    resolver: yupResolver<AdmPwdUpdateRequestDto>(
      yup.object().shape({
        newPassword: yup
          .string()
          .matches(
            /^(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{6,}$/,
            "비밀번호는 영문과 숫자, 특수기호 조합하여 6자리 이상 입력하셔야 합니다."
          )
          .required("비밀번호를 입력해주세요."),
        newPasswordConfirm: yup
          .string()
          .oneOf([yup.ref("newPassword")], "비밀번호가 일치하지 않습니다.")
          .matches(
            /^(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{6,}$/,
            "비밀번호 확인은 영문과 숫자, 특수기호 조합하여 6자리 이상 입력하셔야 합니다."
          )
          .required("비밀번호 확인을 입력해주세요."),
      })
    ),
  });

  /**
   * 관리자 정보 업데이트 이벤트 핸들러
   * @returns {Promise<void>}
   */
  const handleAdmUpdate = async () => {
    const isValid = await updateMethods.trigger();

    if (isValid) {
      const data = updateMethods.getValues();
      if (admInfo) {
        onAdmUpdate(data, admInfo.admIdntfcId.toString());
      }
    }
  };

  /**
   * 관리자 비밀번호 업데이트 이벤트 핸들러
   * @returns {Promise<void>}
   */
  const handleAdmPwdUpdate = async () => {
    const isValid = await pwdUpdateMethods.trigger();

    if (isValid) {
      const data = pwdUpdateMethods.getValues();
      if (admInfo) {
        onAdmPwdUpdate(admInfo.admIdntfcId.toString(), data);
      }
    }
  };

  return {
    updateMethods,
    pwdUpdateMethods,
    handleAdmUpdate,
    handleAdmPwdUpdate,
  };
}
